import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CustomHttpHeaders } from 'cl-common-angular';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { first, tap } from 'rxjs/operators';
import { PaymentMethodType } from '../../constants/lookups';
import { CampaignLimits } from '../campaign-limits';
import { MakePayment } from './make-payment';
import { Payment } from './payment';
import { PaymentAccountInfo } from './payment-account-info';
import { PaymentConfig } from './payment-config';
import { PaymentProviderType } from './payment-provider-type';
import { SetupPaymentAccount } from './setup-payment-account';
import { WePayEnvironment } from './we-pay-environment';
declare let WePay: any;

@Injectable({
    providedIn: 'root'
})
export class PaymentService {

    private readonly routePrefix: string = '/api/payment/';

    private readonly _paymentConfig = new BehaviorSubject<PaymentConfig | null>(null);

    private readonly _paymentAccountInfo = new Subject<PaymentAccountInfo | null>();

    constructor(private httpClient: HttpClient) {
    }

    get paymentConfig(): Observable<PaymentConfig | null> {

        if (this._paymentConfig.value) {
            return this._paymentConfig.asObservable();
        }

        return this.httpClient.get<PaymentConfig>(`${this.routePrefix}config`).pipe(tap(config => {
            if (config?.providers?.wePay) {
                // Init the WePay SDK so subsequent calls to WePay hit the right endpoint.
                WePay.set_endpoint(config.providers.wePay.environment);
            }

            // TODO: future SDKs?
            // OtherSdk.init();

            // At this point, our configuration is all ready to go.  Let's pass it to the subject.
            this._paymentConfig.next(config);
        }
        ));
    }

    getPaymentAccountInfo(refresh = false): Observable<PaymentAccountInfo | null> {
        if (refresh) {
            const subscription = this.httpClient.get<PaymentAccountInfo>(`${this.routePrefix}account`).subscribe(
                {
                    next: p => { this._paymentAccountInfo.next(p); },
                    complete: () => { subscription.unsubscribe(); }
                }
            );
        }

        return this._paymentAccountInfo.asObservable();
    }

    setupPaymentAccount = (data: SetupPaymentAccount) => this.httpClient.post<PaymentAccountInfo>(`${this.routePrefix}account`, data)
        .pipe(tap(a => this._paymentAccountInfo.next(a)))

    detachDeletePaymentAccount(deleteAccount: boolean, handleErrorCodes = '') {
        const headers = new HttpHeaders();
        const params = new HttpParams();

        const options = {
            headers,
            params
        };

        if (handleErrorCodes && handleErrorCodes.length) {
            options.headers = headers.set(CustomHttpHeaders.suppressedErrorCodes, handleErrorCodes);
        }

        if (!deleteAccount) {
            options.params = params.set('detach', 'true');
        }

        return this.httpClient.delete(`${this.routePrefix}account`,
            options)
            .pipe(tap(() => { this._paymentAccountInfo.next(null); }));
    }

    async makePayment(payment: Payment, disableSpinner: boolean, handleErrorCodes: string): Promise<CampaignLimits> {

        if (payment.paymentMethod.paymentMethodType !== PaymentMethodType.CreditCard) {
            throw new Error('Currently, only credit card payment methods are supported');
        }

        const config = await this.paymentConfig.pipe(first()).toPromise();

        if (!config?.providerType) {
            throw new Error('Missing payment-provider config');
        }

        const makePaymentData = new MakePayment();
        makePaymentData.idempotentKey = payment.idempotentKey;
        makePaymentData.campaignId = payment.campaignId;
        makePaymentData.amount = payment.amount;
        makePaymentData.donor = payment.donor;
        makePaymentData.fee = payment.fee;
        makePaymentData.isPrivate = payment.isPrivate;

        let promise: Promise<void> | null = null;

        switch (config.providerType) {
            case PaymentProviderType.wePay:
                promise = this.preProcessPaymentWePay(config, payment, makePaymentData);
                break;
            default:
                throw new Error(`Unsupported payment-provider type: ${config.providerType}`);
        }

        await promise;

        // At this point, we have done pre-processing with the provider.  For example, for WePay this
        // means we've tokenized the credit-card data.  Let's proceed with making the actualy payment.
        // This is managed on the backend.

        let headers = new HttpHeaders();

        let options = {};

        if (disableSpinner) {
            headers = headers.set(CustomHttpHeaders.disableSpinner, 'Y');
        }

        if (handleErrorCodes && handleErrorCodes.length) {
            headers = headers.set(CustomHttpHeaders.suppressedErrorCodes, handleErrorCodes);
        }

        if (headers.keys().length !== 0) {
            options = { headers };
        }

        return await this.httpClient.post<CampaignLimits>(`${this.routePrefix}`, makePaymentData, options).pipe(first()).toPromise() ?? {} as CampaignLimits;
    }

    preProcessPaymentWePay(config: PaymentConfig, payment: Payment, makePaymentData: MakePayment): Promise<void> {

        const wePayConfig = config.providers.wePay;
        const creditCard = payment.paymentMethod.creditCard;

        if (wePayConfig.environment === WePayEnvironment.Staging && wePayConfig.testing?.testCreditCards) {
            makePaymentData.isTestCreditCard = wePayConfig.testing?.testCreditCards?.indexOf(
                payment.paymentMethod?.creditCard?.creditCardNumber) >= 0;
        }

        return new Promise<void>((resolve, reject) => {

            // To limit our PCI footprint, by design we never send the credit-card or bank information to
            // our servers.  Instead, we send a request directly to WePay to tokenize the credit-card
            // information for us.  We then send the tokenized data to our server to complete the
            // transaction with WePay and persist related information.  In this way, our backend never
            // even touches the user's sensitive payment information

            WePay.credit_card.create({
                client_id: wePayConfig.wePayClientId,
                user_name: creditCard.nameOnCard,
                email: creditCard.email,
                cc_number: creditCard.creditCardNumber,
                cvv: creditCard.cvv,
                expiration_month: creditCard.expirationMonth,
                expiration_year: creditCard.expirationYear,
                address: {
                    postal_code: creditCard.zipCode
                }
            }, (data: any) => {

                if (data.error) {
                    reject({ ccTokenizeFailure: data });
                } else {
                    makePaymentData.paymentMethodType = PaymentMethodType.CreditCard;
                    makePaymentData.paymentMethodToken = data.credit_card_id.toString();

                    resolve(data);
                }
            });
        });
    }

    getNewId(disableSpinner = false): Observable<string> {
        let options = {};

        if (disableSpinner) {
            options = { headers: new HttpHeaders({ [CustomHttpHeaders.disableSpinner]: 'Y' }) };
        }

        return this.httpClient.get<string>(`${this.routePrefix}newid`, options);
    }
}
